import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import type { User } from '@tips-crm/shared';

// API response envelope (mirrors CONVENTIONS.md).
export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export const TOKEN_KEY = 'tips_crm_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore storage failures (private mode etc.) */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    // config.headers is an AxiosHeaders instance in axios v1; .set is the typed mutator.
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearToken();
      // Avoid redirect loops on the login page itself.
      if (!window.location.pathname.startsWith('/login')) {
        const redirect = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/login?redirect=${redirect}`);
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Probe `/auth/me` WITHOUT the global 401 interceptor. Used to verify an SSO session
 * at login: on success the bearer maps to a real CRM user; on failure (e.g. a ZITADEL
 * identity with no CRM account) the caller logs out cleanly instead of fabricating a
 * session — which previously caused a 401 → redirect → re-bridge reload loop.
 */
export async function fetchMeRaw(): Promise<User> {
  const token = getToken();
  const res = await axios.get<ApiEnvelope<User>>(`${api.defaults.baseURL ?? '/api'}/auth/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const body = res.data;
  if (!body.success || !body.data) {
    throw new Error(body.message || body.error || 'Not authorized');
  }
  return body.data;
}

/** Normalize an axios/error into a human-readable message. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiEnvelope<unknown> | undefined;
    return data?.message || data?.error || err.message || 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}

// ---- Typed helpers ---------------------------------------------------------
// Server replies with { success, data }. These unwrap `data` for the caller.

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const resp = await promise;
  const body = resp.data;
  if (!body.success) {
    throw new Error(body.message || body.error || 'Request failed');
  }
  return body.data as T;
}

export function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.get<ApiEnvelope<T>>(url, config));
}

export function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.post<ApiEnvelope<T>>(url, body, config));
}

export function apiPut<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.put<ApiEnvelope<T>>(url, body, config));
}

export function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.patch<ApiEnvelope<T>>(url, body, config));
}

export function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.delete<ApiEnvelope<T>>(url, config));
}
